import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "app-text-field" }
const _hoisted_2 = {
  key: 0,
  class: "field-label"
}

import { computed, onBeforeUnmount, ref, useAttrs, watch } from "vue";
import { searchAddress } from "@/shared/services/http.geo-config";

type AddressSuggestion = {
  title: string;
  subtitle?: string;
  value: string;
  raw: Record<string, any>;
};


export default /*@__PURE__*/_defineComponent({
  ...{
  inheritAttrs: false,
},
  __name: 'AppAddressField',
  props: /*@__PURE__*/_mergeModels({
    defaultValue: { default: null },
    variant: { default: "solo" },
    flat: { type: Boolean, default: true },
    placeholder: { default: "Enter address" },
    hideDetails: { type: [Boolean, String], default: true },
    label: { default: "" },
    labelIcon: { default: "" },
    prependInnerIcon: { default: "mdi-map-marker-outline" }
  }, {
    "modelValue": { default: null },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["selected"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {



const model = _useModel<string | null>(__props, "modelValue");
const emit = __emit;
const search = ref("");
const items = ref<AddressSuggestion[]>([]);
const loading = ref(false);
const attrs = useAttrs();
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let suppressNextSearch = false;
let isApplyingSelection = false;

const props = __props;

const fieldClass = computed(() => ["app-text-field__input", attrs.class]);
const addressModel = computed({
  get: () => (model.value === "" ? null : model.value),
  set: (value: string | null) => {
    model.value = value ?? "";
  },
});
const resolvedPlaceholder = computed(() => props.placeholder?.trim() || "Enter address");
const fieldAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});

watch(
  () => props.defaultValue,
  (next) => {
    const value = next?.trim?.() ?? "";
    if (!value) return;
    if (model.value == null || model.value === "") {
      model.value = value;
    }
  },
  { immediate: true },
);

function formatSuggestion(item: Record<string, any>) {
  return String(item?.full_address || "").trim();
}

function toSuggestion(item: Record<string, any>): AddressSuggestion {
  const title = formatSuggestion(item);
  const subtitle = String([item?.city, item?.state, item?.zipcode, item?.country].filter(Boolean).join(", ")).trim();

  return {
    title,
    subtitle,
    value: title,
    raw: item,
  };
}

function normalizeResponse(data: any): Record<string, any>[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function handleSelect(next: string | null) {
  const selectedItem = items.value.find((item) => item.value === next) ?? null;
  const payload = selectedItem?.raw ?? null;
  isApplyingSelection = true;
  suppressNextSearch = true;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = null;
  emit("selected", payload);
  queueMicrotask(() => {
    isApplyingSelection = false;
  });
}

async function runSearch(query: string) {
  const value = query.trim();
  if (!value) {
    items.value = [];
    return;
  }

  loading.value = true;
  try {
    const data = await searchAddress({
      full_address: value,
      limit: 10,
    });
    items.value = normalizeResponse(data).map(toSuggestion);
  } catch (err) {
    const message = String((err as any)?.message ?? err ?? "");
    const isTimeout = message.includes("timeout") || message.includes("exceeded");

    if (isTimeout) {
      console.warn("AppAddressField search timed out");
    } else {
      console.error("AppAddressField search error:", err);
    }
    items.value = [];
  } finally {
    loading.value = false;
  }
}

watch(search, (next) => {
  if (isApplyingSelection) return;

  if (suppressNextSearch) {
    suppressNextSearch = false;
    return;
  }

  if (next && next.trim() === (model.value || "").trim()) {
    return;
  }

  if (searchTimer) clearTimeout(searchTimer);

  searchTimer = setTimeout(() => {
    void runSearch(next || "");
  }, 300);
});

watch(model, (next) => {
  suppressNextSearch = true;
  search.value = next ?? "";
});

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer);
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (__props.labelIcon)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 0,
                size: "18"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(__props.labelIcon), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(__props.label), 1)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_autocomplete, _mergeProps({
      modelValue: addressModel.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((addressModel).value = $event)),
      search: search.value,
      "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event))
    }, fieldAttrs.value, {
      class: fieldClass.value,
      items: items.value,
      loading: loading.value,
      "hide-no-data": !search.value,
      "menu-props": { maxHeight: 320, contentClass: 'address-autocomplete-menu', eager: true, transition: false },
      "no-filter": true,
      placeholder: resolvedPlaceholder.value,
      "persistent-placeholder": "",
      "hide-details": __props.hideDetails,
      variant: __props.variant,
      flat: __props.flat,
      "menu-icon": items.value.length ? 'mdi-chevron-down' : '',
      "prepend-inner-icon": __props.prependInnerIcon || undefined,
      autocomplete: "off",
      "item-title": "title",
      "item-value": "value",
      "onUpdate:modelValue": handleSelect
    }), {
      item: _withCtx(({ props, item }) => [
        _createVNode(_component_v_list_item, _mergeProps(props, {
          class: "address-autocomplete-item",
          density: "compact",
          title: item.title,
          subtitle: item.raw?.subtitle
        }), {
          prepend: _withCtx(() => [
            _createVNode(_component_v_icon, {
              size: "18",
              color: "primary"
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode("mdi-map-marker-outline", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 16, ["title", "subtitle"])
      ]),
      _: 1
    }, 16, ["modelValue", "search", "class", "items", "loading", "hide-no-data", "placeholder", "hide-details", "variant", "flat", "menu-icon", "prepend-inner-icon"])
  ]))
}
}

})